import { hexToRgb } from '@/common/utils';

// Human readable names for the colors schools pick for their products. Product colors are stored
// as plain hex, so a name is derived by snapping the hex to the closest entry in this palette.
const COLOR_NAMES = [
  { name: 'Black', hex: '#000000' },
  { name: 'Charcoal', hex: '#333333' },
  { name: 'Slate', hex: '#4A4A4A' },
  { name: 'Gray', hex: '#808080' },
  { name: 'Silver', hex: '#C0C0C0' },
  { name: 'White', hex: '#FFFFFF' },
  { name: 'Ivory', hex: '#FFFFF0' },
  { name: 'Cream', hex: '#FFFDD0' },
  { name: 'Beige', hex: '#F5F5DC' },
  { name: 'Sand', hex: '#E0CDA9' },
  { name: 'Tan', hex: '#D2B48C' },
  { name: 'Camel', hex: '#C19A6B' },
  { name: 'Khaki', hex: '#BDB76B' },
  { name: 'Brown', hex: '#795548' },
  { name: 'Chocolate', hex: '#7B3F00' },
  { name: 'Rust', hex: '#B7410E' },
  { name: 'Burgundy', hex: '#800020' },
  { name: 'Wine', hex: '#722F37' },
  { name: 'Maroon', hex: '#B03060' },
  { name: 'Crimson', hex: '#DC143C' },
  { name: 'Coral Red', hex: '#E84B3C' },
  { name: 'Red', hex: '#E53935' },
  { name: 'Rose', hex: '#FF6F91' },
  { name: 'Blush', hex: '#F4C2C2' },
  { name: 'Pink', hex: '#FFC0CB' },
  { name: 'Magenta', hex: '#D81B60' },
  { name: 'Plum', hex: '#8E4585' },
  { name: 'Purple', hex: '#8E43AD' },
  { name: 'Violet', hex: '#7F4FC9' },
  { name: 'Lavender', hex: '#C9A9E9' },
  { name: 'Mauve', hex: '#B784A7' },
  { name: 'Indigo', hex: '#4B0082' },
  { name: 'Midnight', hex: '#191970' },
  { name: 'Navy', hex: '#222F3D' },
  { name: 'Denim', hex: '#3E5C76' },
  { name: 'Steel Blue', hex: '#4682B4' },
  { name: 'Royal Blue', hex: '#3B86F5' },
  { name: 'Sky Blue', hex: '#3398DB' },
  { name: 'Powder Blue', hex: '#B0E0E6' },
  { name: 'Aqua', hex: '#7FDBFF' },
  { name: 'Turquoise', hex: '#40E0D0' },
  { name: 'Teal', hex: '#1FBC9C' },
  { name: 'Mint', hex: '#B2F0EA' },
  { name: 'Sage', hex: '#9CAF88' },
  { name: 'Emerald', hex: '#2ECC70' },
  { name: 'Forest Green', hex: '#228B22' },
  { name: 'Olive', hex: '#808000' },
  { name: 'Lime', hex: '#A4C639' },
  { name: 'Sunflower', hex: '#F1C40F' },
  { name: 'Gold', hex: '#D4AF37' },
  { name: 'Amber Gold', hex: '#E0A526' },
  { name: 'Mustard', hex: '#E1AD01' },
  { name: 'Orange', hex: '#F39C19' },
  { name: 'Peach', hex: '#FFCBA4' },
  { name: 'Apricot', hex: '#FBCEB1' },
];

const PALETTE = COLOR_NAMES.map(entry => ({ ...entry, rgb: hexToRgb(entry.hex) }));

function colorDistance(a, b) {
  // Redmean: a cheap approximation of perceptual distance, closer to how the eye reads color
  // than a plain RGB distance.
  const meanRed = (a.r + b.r) / 2;
  const deltaR = a.r - b.r;
  const deltaG = a.g - b.g;
  const deltaB = a.b - b.b;

  return (2 + meanRed / 256) * deltaR * deltaR + 4 * deltaG * deltaG + (2 + (255 - meanRed) / 256) * deltaB * deltaB;
}

// Schools can name each of their product colors themselves; the palette name is only a fallback for
// colors saved before naming existed, or left unnamed.
export function getProductColorName(product, hex) {
  const colors = (product && product.colors) || [];
  const colorNames = (product && product.color_names) || [];
  const index = colors.indexOf(hex);

  return (index !== -1 && colorNames[index]) || getColorName(hex);
}

export function getColorName(hex) {
  const rgb = hexToRgb(hex || '');
  if (!rgb) return '';

  let closest = PALETTE[0];
  let closestDistance = Infinity;

  PALETTE.forEach(entry => {
    if (!entry.rgb) return;

    const distance = colorDistance(rgb, entry.rgb);
    if (distance < closestDistance) {
      closestDistance = distance;
      closest = entry;
    }
  });

  return closest.name;
}
